(function () {
  'use strict';

  const CONSULTATION_API_BASE = 'https://tara-ai-consultation-worker.nawaf9970.workers.dev';

  const FRIENDLY_MESSAGES_EN = Object.freeze({
    rate_limited_email: 'Too many requests for this email - please wait a minute.',
    rate_limited_ip: 'Too many requests - please wait a moment.',
    rate_limited_assessment_route_ip: 'Daily consultation limit reached - please come back tomorrow.',
    rate_limited_transcribe_route_ip: 'Voice transcription limit reached - please type your answer instead.',
    rate_limited_question_route_ip: 'Too many questions in a short time - please wait a moment.',
    rate_limited_my_consultations_route_ip: 'Please wait a moment.',
    assessment_already_running: 'Your consultation is still processing - please wait.',
    body_too_large: 'Your input was too large.',
    audio_too_large: 'Your voice note was too large (max 25 MB).',
    bad_json: 'Something went wrong with your submission. Please try again.',
    app_proxy_invalid_signature: 'Please refresh the page and try again.',
    consultation_kill_switch_active: 'The consultation service is temporarily unavailable. Please try again shortly.',
  });

  const FRIENDLY_MESSAGES_ES = Object.freeze({
    rate_limited_email: 'Demasiadas peticiones para este email — espera un minuto.',
    rate_limited_ip: 'Demasiadas peticiones — espera un momento.',
    rate_limited_assessment_route_ip: 'Has alcanzado el límite diario de consultas — vuelve mañana.',
    rate_limited_transcribe_route_ip: 'Límite de transcripción de voz alcanzado — escribe tu respuesta.',
    rate_limited_question_route_ip: 'Demasiadas preguntas en poco tiempo — espera un momento.',
    rate_limited_my_consultations_route_ip: 'Espera un momento.',
    assessment_already_running: 'Tu consulta se está procesando — un momento, por favor.',
    body_too_large: 'Tu mensaje es demasiado largo.',
    audio_too_large: 'Tu nota de voz es demasiado larga (máx. 25 MB).',
    bad_json: 'Algo no está bien con tu envío. Inténtalo de nuevo.',
    app_proxy_invalid_signature: 'Recarga la página e inténtalo de nuevo.',
    consultation_kill_switch_active: 'El servicio de consulta está temporalmente no disponible. Inténtalo más tarde.',
  });

  const FRIENDLY_MESSAGES = Object.freeze({
    en: FRIENDLY_MESSAGES_EN,
    es: FRIENDLY_MESSAGES_ES,
  });

  const FRIENDLY_MESSAGE_KEYS = Object.freeze({
    rate_limited_email: 'errorRateLimitedEmail',
    rate_limited_ip: 'errorRateLimitedIp',
    rate_limited_assessment_route_ip: 'errorRateLimitedAssessment',
    rate_limited_transcribe_route_ip: 'errorRateLimitedTranscribe',
    rate_limited_question_route_ip: 'errorRateLimitedQuestion',
    rate_limited_my_consultations_route_ip: 'errorRateLimitedMyConsultations',
    assessment_already_running: 'errorAssessmentAlreadyRunning',
    body_too_large: 'errorBodyTooLarge',
    audio_too_large: 'errorAudioTooLarge',
    bad_json: 'errorBadJson',
    app_proxy_invalid_signature: 'errorAppProxyInvalidSignature',
    consultation_kill_switch_active: 'errorConsultationKillSwitchActive',
  });

  function normalizeBaseUrl(baseUrl) {
    return String(baseUrl || CONSULTATION_API_BASE).trim().replace(/\/+$/, '');
  }

  function resolveUrl(pathOrUrl, baseUrl) {
    const raw = String(pathOrUrl || '').trim();
    if (/^https?:\/\//i.test(raw)) return raw;
    const path = raw.charAt(0) === '/' ? raw : `/${raw}`;
    return `${normalizeBaseUrl(baseUrl)}${path}`;
  }

  function getErrorCode(error) {
    return String(error?.code || error?.errorCode || error?.error_code || '').trim();
  }

  function getMessageLocale() {
    const locale = String(
      (window.Shopify && window.Shopify.locale)
      || document.documentElement.lang
      || navigator.language
      || ''
    ).trim().toLowerCase();
    return locale.startsWith('es') ? 'es' : 'en';
  }

  function friendlyError(error, i18n) {
    const code = getErrorCode(error);
    const key = FRIENDLY_MESSAGE_KEYS[code];
    if (key && i18n && typeof i18n[key] === 'string' && i18n[key].trim()) {
      return i18n[key].trim();
    }
    const messages = FRIENDLY_MESSAGES[getMessageLocale()] || FRIENDLY_MESSAGES_EN;
    if (code && messages[code]) return messages[code];
    return (i18n && typeof i18n.errorGeneric === 'string' && i18n.errorGeneric.trim())
      ? i18n.errorGeneric.trim()
      : (getMessageLocale() === 'es' ? 'Algo ha ido mal. Inténtalo de nuevo.' : 'Something went wrong. Please try again.');
  }

  function normalizeErrorShape(error) {
    if (!error || typeof error !== 'object') return error;
    const code = getErrorCode(error);
    if (code) {
      error.code = code;
      error.errorCode = code;
      error.error_code = code;
    }
    return error;
  }

  function syncReturnedSessionId(data) {
    const sessionId = data && typeof data.session_id === 'string' ? data.session_id.trim() : '';
    if (!sessionId) return;

    try {
      if (window.TaraConsultationSession && typeof window.TaraConsultationSession.setStoredSessionId === 'function') {
        window.TaraConsultationSession.setStoredSessionId(sessionId);
      } else {
        sessionStorage.setItem('taraConsultationSessionId', sessionId);
        sessionStorage.setItem('tara_session_id', sessionId);
      }
    } catch (error) {
      /* session storage can be unavailable in private contexts */
    }
  }

  function normalizeRequestError(error, signal, fallbackMessage) {
    const aborted = !!(signal && signal.aborted);
    const abortReason = aborted ? signal.reason : '';

    if (error instanceof Error) {
      if (aborted && !error.abortReason) {
        error.abortReason = abortReason || '';
      }
      if ((aborted || error.name === 'AbortError') && !error.errorCode) {
        error.errorCode = abortReason === 'request-timeout' ? 'request_timeout' : 'request_aborted';
      }
      return normalizeErrorShape(error);
    }

    const normalized = new Error(
      (typeof error === 'string' && error.trim())
        || (typeof abortReason === 'string' && abortReason.trim())
        || fallbackMessage
        || 'Request failed'
    );

    normalized.name = aborted ? 'AbortError' : 'Error';
    normalized.abortReason = abortReason || (typeof error === 'string' ? error : '');
    normalized.errorCode = aborted && abortReason === 'request-timeout' ? 'request_timeout' : (aborted ? 'request_aborted' : '');
    normalized.originalError = error;
    return normalizeErrorShape(normalized);
  }

  function withRequestDuration(error, signal, fallbackMessage, durationMs) {
    let normalizedError = normalizeRequestError(error, signal, fallbackMessage);
    if (!normalizedError || (typeof normalizedError !== 'object' && typeof normalizedError !== 'function')) {
      normalizedError = normalizeRequestError(String(normalizedError || ''), signal, fallbackMessage);
    }

    normalizedError.requestDurationMs = durationMs || 0;
    return normalizedError;
  }

  async function buildApiError(response, fallbackMessage) {
    let payload = null;
    try {
      payload = await response.clone().json();
    } catch (error) {
      payload = null;
    }

    const message = payload && typeof payload.error === 'string' && payload.error.trim()
      ? payload.error.trim()
      : `${fallbackMessage || 'API error'} (${response.status})`;

    const apiError = new Error(message);
    apiError.name = 'ConsultationApiError';
    apiError.status = response.status;
    apiError.workerError = payload && typeof payload.error === 'string' ? payload.error.trim() : '';
    apiError.errorCode = payload && typeof payload.error_code === 'string' ? payload.error_code.trim() : '';
    return normalizeErrorShape(apiError);
  }

  async function parseResponse(response, fallbackMessage) {
    if (!response.ok) {
      throw await buildApiError(response, fallbackMessage);
    }
    return response.json();
  }

  async function postJson(options) {
    const startTimer = typeof options.startTimer === 'function' ? options.startTimer : null;
    const finishTimer = startTimer ? startTimer() : null;
    let durationMs = 0;
    const url = resolveUrl(options.path || options.url, options.baseUrl);

    try {
      const response = await fetch(url, {
        method: options.method || 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(options.headers || {}),
        },
        body: JSON.stringify(options.body || {}),
        signal: options.signal,
      });

      if (finishTimer) {
        durationMs = finishTimer({
          status_code: response.status,
          slow_threshold_ms: options.slowThresholdMs,
          extra: options.timerExtra,
        });
      }

      const data = await parseResponse(response, options.fallbackMessage);
      syncReturnedSessionId(data);
      return { response, data, durationMs };
    } catch (error) {
      let normalizedError = normalizeRequestError(error, options.signal, options.fallbackMessage);
      if (!durationMs && finishTimer) {
        durationMs = finishTimer({
          skip_slow_report: true,
          status_code: normalizedError && normalizedError.status ? normalizedError.status : null,
          extra: options.timerExtra,
        });
      }

      throw withRequestDuration(normalizedError, options.signal, options.fallbackMessage, durationMs);
    }
  }

  function parseStreamEventBlock(block) {
    const lines = String(block || '').split('\n');
    let eventName = 'message';
    const dataLines = [];

    lines.forEach((line) => {
      if (line.startsWith('event:')) {
        eventName = line.slice(6).trim() || eventName;
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).trimStart());
      }
    });

    let data = {};
    if (dataLines.length) {
      try {
        data = JSON.parse(dataLines.join('\n'));
      } catch (error) {
        data = { raw: dataLines.join('\n') };
      }
    }

    return { eventName, data };
  }

  function buildStreamEventError(data, fallbackMessage) {
    const streamError = new Error(
      data?.error || fallbackMessage || 'Streaming request failed'
    );
    streamError.name = 'ConsultationStreamError';
    streamError.status = data?.status || 500;
    streamError.workerError = data?.error || '';
    streamError.errorCode = data?.error_code || 'stream_error';
    streamError.streamFinalError = true;
    return normalizeErrorShape(streamError);
  }

  async function postEventStream(options) {
    const startTimer = typeof options.startTimer === 'function' ? options.startTimer : null;
    const finishTimer = startTimer ? startTimer() : null;
    const streamEvents = [];
    let durationMs = 0;
    // Different worker streams use different terminal event names —
    // /api/question-stream emits `final_question`, /api/assessment-stream
    // emits `final` (and may also emit `cache_hit` as an early terminal).
    const finalEventNames = Array.isArray(options.finalEventNames) && options.finalEventNames.length
      ? options.finalEventNames
      : ['final_question'];
    const url = resolveUrl(options.path || options.url, options.baseUrl);

    function isFinalEvent(name) {
      return finalEventNames.indexOf(name) !== -1;
    }

    try {
      const response = await fetch(url, {
        method: options.method || 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          ...(options.headers || {}),
        },
        body: JSON.stringify(options.body || {}),
        signal: options.signal,
      });

      if (!response.ok) {
        const apiError = await buildApiError(response, options.fallbackMessage);
        apiError.streamHttpFailure = true;
        throw apiError;
      }

      if (!response.body || typeof response.body.getReader !== 'function') {
        const streamError = new Error('Streaming response body is unavailable');
        streamError.name = 'ConsultationStreamUnavailable';
        streamError.streamUnavailable = true;
        throw streamError;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let finalPayload = null;
      let finalEventName = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');
        let separatorIndex = buffer.indexOf('\n\n');

        while (separatorIndex !== -1) {
          const block = buffer.slice(0, separatorIndex);
          buffer = buffer.slice(separatorIndex + 2);
          separatorIndex = buffer.indexOf('\n\n');

          if (!block.trim()) continue;
          const event = parseStreamEventBlock(block);
          streamEvents.push(event);
          if (typeof options.onEvent === 'function') {
            options.onEvent(event.eventName, event.data);
          }

          if (event.eventName === 'error') {
            throw buildStreamEventError(event.data, options.fallbackMessage);
          }

          if (isFinalEvent(event.eventName)) {
            finalPayload = event.data && typeof event.data === 'object'
              ? (event.data.payload != null ? event.data.payload : event.data)
              : null;
            finalEventName = event.eventName;
          }
        }
      }

      if (buffer.trim()) {
        const event = parseStreamEventBlock(buffer);
        streamEvents.push(event);
        if (typeof options.onEvent === 'function') {
          options.onEvent(event.eventName, event.data);
        }
        if (event.eventName === 'error') {
          throw buildStreamEventError(event.data, options.fallbackMessage);
        }
        if (isFinalEvent(event.eventName)) {
          finalPayload = event.data && typeof event.data === 'object'
            ? (event.data.payload != null ? event.data.payload : event.data)
            : null;
          finalEventName = event.eventName;
        }
      }

      if (!finalPayload) {
        const streamError = new Error('Streaming response ended without a terminal event');
        streamError.name = 'ConsultationStreamProtocolError';
        streamError.streamProtocolError = true;
        throw streamError;
      }

      if (finishTimer) {
        durationMs = finishTimer({
          status_code: response.status,
          slow_threshold_ms: options.slowThresholdMs,
          extra: {
            ...(options.timerExtra || {}),
            stream_event_count: streamEvents.length,
            stream_final_event: finalEventName,
          },
        });
      }

      syncReturnedSessionId(finalPayload);
      return { response, data: finalPayload, durationMs, streamEvents, finalEventName };
    } catch (error) {
      let normalizedError = normalizeRequestError(error, options.signal, options.fallbackMessage);
      if (!durationMs && finishTimer) {
        durationMs = finishTimer({
          skip_slow_report: true,
          status_code: normalizedError && normalizedError.status ? normalizedError.status : null,
          extra: {
            ...(options.timerExtra || {}),
            stream_event_count: streamEvents.length,
          },
        });
      }

      throw withRequestDuration(normalizedError, options.signal, options.fallbackMessage, durationMs);
    }
  }

  async function submitFormData(options) {
    const startTimer = typeof options.startTimer === 'function' ? options.startTimer : null;
    const finishTimer = startTimer ? startTimer() : null;
    let durationMs = 0;
    const url = resolveUrl(options.path || options.url, options.baseUrl);

    try {
      const response = await fetch(url, {
        method: options.method || 'POST',
        body: options.body,
        signal: options.signal,
        headers: options.headers || {},
      });

      if (finishTimer) {
        durationMs = finishTimer({
          status_code: response.status,
          slow_threshold_ms: options.slowThresholdMs,
          extra: options.timerExtra,
        });
      }

      const data = await parseResponse(response, options.fallbackMessage);
      syncReturnedSessionId(data);
      return { response, data, durationMs };
    } catch (error) {
      let normalizedError = normalizeRequestError(error, options.signal, options.fallbackMessage);
      if (!durationMs && finishTimer) {
        durationMs = finishTimer({
          skip_slow_report: true,
          status_code: normalizedError && normalizedError.status ? normalizedError.status : null,
          extra: options.timerExtra,
        });
      }

      throw withRequestDuration(normalizedError, options.signal, options.fallbackMessage, durationMs);
    }
  }

  window.TaraConsultationApi = Object.freeze({
    baseUrl: CONSULTATION_API_BASE,
    FRIENDLY_MESSAGES,
    resolveUrl,
    friendlyError,
    buildApiError,
    postEventStream,
    postJson,
    submitFormData,
  });
})();
